import type { CalendarDateRangePreset } from 'src/modules/shared/types/date-range.type';
import {
  parseCalendarDateToken,
  toCalendarDateToken,
} from 'src/modules/shared/utils/date-range.util';
import {
  ExecutionDatePreset,
  type ExecutionCalendarDateRange,
  type ExecutionDatePresetId,
  type ExecutionDateQuery,
} from 'src/modules/automations/types/executions/execution-date-filter.type';

const CALENDAR_TOKEN_PREFIX_PATTERN = /^\d{4}-\d{2}-\d{2}/;

/** Normalizes a calendar token or legacy ISO timestamp to `YYYY-MM-DD`. */
export const normalizeExecutionDateToken = (
  value: string | undefined
): string | undefined => {
  if (!value) return undefined;
  const token = CALENDAR_TOKEN_PREFIX_PATTERN.exec(value)?.[0];
  return parseCalendarDateToken(token) ? token : undefined;
};

/** Builds execution date presets relative to the user's local calendar. */
export const buildExecutionDatePresets = (
  labels: Record<ExecutionDatePresetId, string>,
  now: Date
): CalendarDateRangePreset<ExecutionDatePresetId>[] => {
  const today = new Date(now);
  today.setHours(12, 0, 0, 0);
  const subtractDays = (days: number): Date => {
    const date = new Date(today);
    date.setDate(date.getDate() - days);
    return date;
  };
  const to = toCalendarDateToken(today);

  return [
    {
      id: ExecutionDatePreset.TODAY,
      label: labels[ExecutionDatePreset.TODAY],
      value: { from: to, to },
    },
    {
      id: ExecutionDatePreset.LAST_7_DAYS,
      label: labels[ExecutionDatePreset.LAST_7_DAYS],
      value: { from: toCalendarDateToken(subtractDays(6)), to },
    },
    {
      id: ExecutionDatePreset.LAST_30_DAYS,
      label: labels[ExecutionDatePreset.LAST_30_DAYS],
      value: { from: toCalendarDateToken(subtractDays(29)), to },
    },
  ];
};

/** Expands a route calendar range to inclusive local-day API timestamps. */
export const toExecutionDateQuery = (
  range: ExecutionCalendarDateRange
): ExecutionDateQuery => {
  const from = parseCalendarDateToken(range.from);
  const to = parseCalendarDateToken(range.to);

  if (!from || !to) return {};

  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);

  return { from: from.toISOString(), to: to.toISOString() };
};
